import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import Button from 'carbon-react/lib/components/button';
import { LoadingWizardScreen } from '@futrli/ui-components/components/sidebar/wizard/screens/loading-wizard-screen/loading-wizard-screen';
import {
  WizardScreen
} from '@futrli/ui-components/components/sidebar/wizard/screens/wizard-screen/wizard-screen';
import { WizardFinished } from '@futrli/ui-components/components/sidebar/wizard/wizard-finished/wizard-finished';
import { ReportSchedulingView } from '@futrli/views/ReportSchedulingView';
import { MembersView } from '@futrli/views/MembersView';
import { useApiView } from '@futrli/views/hooks/useApiView';

import { useRouteIds } from '../../../hooks/useRouteIds';
import { useScheduleReportForm } from './hooks/useScheduleReportForm';
import { ScheduleFormScreen } from './screens/schedule-form-screen';

export interface UpdateScheduleContainerProps {
  onClose: () => void;
  reportId: string;
  reportName: string;
  scheduleId: string;
}

type Screen = 'form' | 'loading' | 'success';

export const UpdateScheduleContainer = ({
  onClose,
  reportId,
  reportName,
  scheduleId
}: UpdateScheduleContainerProps) => {
  const intl = useIntl();
  const { workspaceId, organisationId, isConsolidation } = useRouteIds();

  const view = useApiView(
    useMemo(
      () =>
        new ReportSchedulingView(workspaceId, organisationId, isConsolidation, {
          schedules: true,
          awaitingReview: false,
          dispatched: false,
          exported: false
        }),
      [workspaceId, organisationId, isConsolidation]
    )
  );

  const membersView = useApiView(
    useMemo(() => new MembersView(workspaceId, organisationId, isConsolidation), [
      workspaceId,
      organisationId,
      isConsolidation
    ])
  );

  const initialSchedule = useMemo(
    () => view.state.scheduledReports.find(s => s.id === scheduleId),
    [scheduleId, view.state.scheduledReports]
  );

  const [screen, setScreen] = useState<Screen>('form');
  const [error, setError] = useState<string | undefined>();

  const form = useScheduleReportForm({
    reportId,
    reportName,
    initialSchedule,
    members: membersView.state.members
  });

  useEffect(() => {
    if (view.state.isUpdatingSchedule) {
      setScreen('loading');
    }
  }, [view.state.isUpdatingSchedule]);

  useEffect(() => {
    if (screen === 'loading' && !view.state.isUpdatingSchedule && !view.state.errorUpdatingSchedule) {
      setScreen('success');
    }
  }, [screen, view.state.isUpdatingSchedule, view.state.errorUpdatingSchedule]);

  useEffect(() => {
    if (view.state.errorUpdatingSchedule) {
      setError(intl.formatMessage({ id: 'reporting.try_again', defaultMessage: 'Try again.' }));
      setScreen('form');
    }
  }, [view.state.errorUpdatingSchedule, intl]);

  const handleSubmit = useCallback(() => {
    const payload = form.buildPayload();
    if (!payload) {
      return;
    }

    if (!initialSchedule) {
      return;
    }

    setError(undefined);
    view.updateSchedule({ scheduleReportId: initialSchedule.id, data: payload });
  }, [form, initialSchedule, view]);

  if (membersView.state.isLoading) {
    return <LoadingWizardScreen />;
  }

  if (screen === 'loading') {
    return (
      <LoadingWizardScreen
        text={intl.formatMessage({ id: 'predict.schedule_saving_changes', defaultMessage: 'Saving changes...' })}
      />
    );
  }

  if (screen === 'success') {
    return (
      <WizardScreen
        rightSideButtons={
          <Button buttonType="primary" onClick={onClose}>
            {intl.formatMessage({ id: 'common.close', defaultMessage: 'Close' })}
          </Button>
        }
      >
        <WizardFinished
          text={intl.formatMessage({ id: 'predict.schedule_changes_saved', defaultMessage: 'Changes saved' })}
        />
      </WizardScreen>
    );
  }

  return (
    <ScheduleFormScreen
      reportName={reportName}
      members={membersView.state.members}
      form={form}
      isEditing
      error={error}
      onCancel={onClose}
      onSubmit={handleSubmit}
    />
  );
};
